/* eslint-disable @typescript-eslint/no-explicit-any */
import { useSelector } from 'react-redux';
import { ECodeType } from '../../store/ducks/user/types';
import { customersAuthorizationApi } from '../api';
import { EndpointsMvp } from './enum';
import { NewCartItem, SortProductsItem } from './models';
import { useAuth } from './useAuth';
import { ApplicationState } from '../../store';
import { useMemo } from 'react';
import { GetFlag } from '../../utils/featureFlag';
import { useShoppingCart } from '../../providers/shoppingcart.provider';
import { useApiInstance } from './useApiInstance';
import { magentoURLBuilder } from '../../utils/magentoUrlBuilder';

export interface HitSource {
  name: string;
  sku: string;
  sap_cod_setor_atividade: string;
  internal_code: string;
}

interface Hits {
  hits: Hit[];
}

export interface Hit {
  _index: string;
  _id: string;
  _score: number | null;
  _source: HitSource;
  sort: string[];
}

interface ResponseProducts {
  status: number;
  hits: Hits;
  data?: any;
}
export type MultProductsResponse = Omit<SortProductsItem, 'salesDate'>;

type ProductsPriceProps = 'all' | 'priced' | 'unpriced' | 'default';

export const useProductForCustomer = () => {
  const productsPriced = GetFlag('search_products_priced');
  const productsUnpriced = GetFlag('search_products_unpriced');
  const newSearchItemsEndpointFlag = GetFlag('new_search_items_endpoint');
  const { updateProductsInformation } = useShoppingCart();
  const commerceApi = useApiInstance();

  const productsPriceFilter = (): ProductsPriceProps => {
    if (productsPriced && productsUnpriced) {
      return 'all';
    }

    if (productsPriced) {
      return 'priced';
    }

    if (productsUnpriced) {
      return 'unpriced';
    }

    return 'default';
  };

  const { getHeaders } = useAuth();
  const headers = getHeaders();

  const { emitterId, receiverId } = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );
  const { customers } = useSelector(
    (appState: ApplicationState) => appState.customers
  );

  const customer = useMemo(
    () => customers.find((c) => c.customerId === emitterId),
    [emitterId]
  );

  const getProductForCustomerSKU = async (
    customerId: string,
    codActivity: string[],
    sku: string[],
    internalCode: string[],
    sortType: string
  ): Promise<ResponseProducts> => {
    const codActivityItems = codActivity.map((cod) => {
      const codNumber = parseInt(cod, 10);
      if (codNumber < 10) {
        cod = codNumber.toString();
      }
      return cod;
    });

    const receiver =
      customer &&
      customer?.purchaseReceivers &&
      customer?.purchaseReceivers.length > 0 &&
      receiverId.length === 0
        ? customer?.purchaseReceivers[0].customerId
        : receiverId;
    const bodyRequest = {
      soldTo: emitterId,
      shipTo: receiver,
      customer_id: customerId,
      cod_setor_atividade: codActivityItems,
      sku: sku,
      internalCode: internalCode,
      sortType: sortType,
      pricingType: productsPriceFilter()
    };

    try {
      const response = await customersAuthorizationApi.post(
        `${EndpointsMvp.PRODUCTS_SEARCH_SKU_OR_INTERNAL_COD}`,
        bodyRequest,
        { headers }
      );
      return {
        status: response.status,
        hits: response.data.hits,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching product for customer:', error);
      throw error;
    }
  };

  const getProductForCustomerInternalCode = async (
    customerId: string,
    internalCode: string[],
    sortType: string,
    codActivity: string[],
    sku?: string[]
  ): Promise<ResponseProducts> => {
    const codActivityItems = codActivity.map((cod) => {
      const codNumber = parseInt(cod, 10);
      if (codNumber < 10) {
        cod = codNumber.toString();
      }
      return cod;
    });

    const receiver =
      customer &&
      customer?.purchaseReceivers &&
      customer?.purchaseReceivers.length > 0 &&
      receiverId.length === 0
        ? customer?.purchaseReceivers[0].customerId
        : receiverId;
    const bodyRequest = {
      soldTo: emitterId,
      shipTo: receiver,
      customer_id: customerId,
      cod_setor_atividade: codActivityItems,
      sku: sku,
      internalCode: internalCode,
      sortType: sortType
    };

    try {
      const response = await customersAuthorizationApi.post(
        `${EndpointsMvp.PRODUCTS_SEARCH_SKU_OR_INTERNAL_COD}`,
        bodyRequest,
        { headers }
      );

      return {
        status: response.status,
        hits: response.data.hits,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching product for customer:', error);
      throw error;
    }
  };

  const fetchProductDetails = async (
    sku: string,
    soldTo: string,
    shipTo: string
  ): Promise<any> => {
    try {
      const response = await customersAuthorizationApi.get(
        `${EndpointsMvp.FETCH_PRODUCT_DETAILS(sku, soldTo, shipTo)}`,
        { headers }
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching product details:', error);
      throw error;
    }
  };

  const fetchProductDetailByCatalog = async (sku: string) => {
    const params = magentoURLBuilder({
      term: sku,
      pageSize: 1,
      typeSearch: 'list'
    });
    const response = await commerceApi.get(
      `${EndpointsMvp.CATALOG(emitterId, receiverId)}`,
      { params, headers: getHeaders({ withCommerceToken: true }) }
    );
    const item = response.data?.items?.[0];
    if (!item) return undefined;
    return {
      ...item,
      price: item?.extension_attributes?.price_gerdau
        ? JSON.parse(item.extension_attributes.price_gerdau)
        : undefined
    };
  };

  const getMultProducts = async (
    sku: string[],
    codeType: ECodeType,
    customer: string,
    pageSize: number,
    currentPage: number,
    codActivity: string[],
    withOptions?: boolean
  ): Promise<MultProductsResponse[]> => {
    const codActivityItems = codActivity.map((cod) => {
      const codNumber = parseInt(cod, 10);
      if (codNumber < 10) {
        cod = codNumber.toString();
      }
      return cod;
    });

    const params = {
      includeOptions: !!withOptions
    };

    try {
      if (newSearchItemsEndpointFlag) {
        const paramsMagento = magentoURLBuilder({
          term: sku.join(' '),
          pageSize: sku.length,
          typeSearch: 'list'
        });

        const responseCatalog = await commerceApi.get(
          `${EndpointsMvp.CATALOG(emitterId, receiverId)}`,
          {
            params: paramsMagento,
            headers: getHeaders({ withCommerceToken: true })
          }
        );

        const data = responseCatalog.data.items.map((item: NewCartItem) => ({
          ...item,
          price: item?.extension_attributes?.price_gerdau
            ? JSON.parse(item?.extension_attributes?.price_gerdau)
            : ''
        }));

        if (data && Array.isArray(data)) {
          updateProductsInformation(data);
        }

        return data;
      }

      const response = await commerceApi.get(
        `${EndpointsMvp.SEARCH_MULT_PRODUCTS(
          sku,
          codeType,
          customer,
          pageSize,
          currentPage,
          codActivityItems
        )}`,
        { headers, params }
      );

      if (response.data && Array.isArray(response.data)) {
        updateProductsInformation(response.data);
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching product details:', error);
      throw error;
    }
  };

  const customerProductIndex = async (customerId: string) => {
    const body = {
      customerId: customerId
    };

    await customersAuthorizationApi.post(
      `${EndpointsMvp.CUSTOMER_PRODUCTS_INDEX}`,
      body,
      { headers }
    );

    return;
  };

  return {
    getProductForCustomerSKU,
    getProductForCustomerInternalCode,
    fetchProductDetails,
    fetchProductDetailByCatalog,
    getMultProducts,
    customerProductIndex
  };
};
